'use client';

import { BrandWordmarkSvg } from '@/assets/logos/icon-wordmark-svg';
import { isAgentHomeProjectBoundAtom } from '@/atoms/sideNavAtoms';
import { useWhiteLabelSafe } from '@/context/white-label';
import {
  IS_INSET_DESIGN_ENABLED,
  useIsNangoIntegrationsEnabled,
  useIsRibbonSidebarEnabled,
} from '@/features/flags';
import { useInfiniteQueryChats } from '@/hooks/query/useInfiniteQueryChats';
import { cn } from '@/lib/cn';
import { parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { useHeidi2BookDemoPort } from '@/utils/use-heidi-2-book-demo-port';
import {
  Heidi2GetStartedChecklistHost,
  type Heidi2GetStartedChecklistPorts,
} from '@heidi/agents';
import {
  currentWorkHomeViewAtom,
  useAllWorkflowRuns,
  useBaseChatHref,
} from '@heidi/agents/data';
import {
  PageShell,
  SIDEBAR_HEADER_ICON_SIZE,
  SIDEBAR_WIDTH_NAV_V3,
  SidebarProvider,
} from '@heidi/design-system/page-shell';
import { TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useQueryNangoConnections } from '@heidi/integrations/connected-apps';
import { useIsMobile } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getDesktopHeaderPaddingClass } from '../desktop-header-padding';
import {
  isAgentHomeLandingAtom,
  shouldShowAgentHomePanel,
  useAgentHomePanel,
} from './agent-home-panel-state';
import { AgentHomePanelToggle } from './agent-home-panel-toggle';
import { NavV3 } from './nav-v3';
import { navV3Messages } from './nav-v3.messages';

const KEEP_PANEL_EXPANDED = () => {};

/**
 * The checklist's progress queries and its book-a-demo port, bound here rather
 * than in the `_app` Heidi II wiring. This is the checklist's only mount, and
 * the workflow-runs hook and the HubSpot meeting queries are reached nowhere
 * else on a page load — binding them at `_app` would put them in the eager
 * cone of every one. Hooks by reference: the progress read gates all three on
 * `enabled`, so they must still run only while the checklist is visible.
 */
const heidi2GetStartedChecklistPorts: Heidi2GetStartedChecklistPorts = {
  useIsNangoIntegrationsEnabled,
  useInfiniteQueryChats,
  useQueryNangoConnections,
  useAllWorkflowRuns,
  parseTimeStampOrNull,
  useHeidi2BookDemoPort,
};

/** Secondary Home navigation mounted beside the primary rail. */
export const AgentHomePanel = () => {
  const intl = useIntl();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { isOpen } = useAgentHomePanel();
  const baseChatHref = useBaseChatHref();
  const isHomeLanding = useAtomValue(isAgentHomeLandingAtom);
  const isProjectBound = useAtomValue(isAgentHomeProjectBoundAtom);
  const isRoutinesView = useAtomValue(currentWorkHomeViewAtom) === 'skills';
  const isTauriDesktop = useMemo(() => isTauri(), []);
  const isInsetDesignEnabled = IS_INSET_DESIGN_ENABLED();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const { isWhiteLabel } = useWhiteLabelSafe();

  if (
    !shouldShowAgentHomePanel({
      asPath: router.asPath,
      baseChatHref,
      isOpen,
      isMobile,
      isHomeLanding,
      isProjectBound,
      isRoutinesView,
    })
  ) {
    return null;
  }

  return (
    <PageShell.Panel
      isPinned
      state="open"
      width={SIDEBAR_WIDTH_NAV_V3}
      isInset={isInsetDesignEnabled}
      id="agent-home-panel"
      data-testid="agent-home-panel"
    >
      {/* In flow below ScribeLayout's titlebar padding, so always shell-anchored. */}
      <div
        data-testid="agent-home-panel-header"
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 p-2',
          isRibbonSidebarEnabled && 'pt-4',
          isTauriDesktop &&
            getDesktopHeaderPaddingClass({ isInsetDesignEnabled, isShellAnchored: true })
        )}
      >
        {/* NavV3 rows indent their icon box by the group p-2 (8px) plus the
            SidebarMenuButton's own p-2 (8px), and the lucide glyph sits a few
            px further in on its own bearing. The header wrapper only has the
            group's 8px, so ms-3 adds the 12px needed to line the wordmark up
            with the visible New chat / Routines / Evidence icon glyphs. */}
        <div
          className="relative ms-3 flex shrink-0 items-center"
          style={{ height: SIDEBAR_HEADER_ICON_SIZE }}
        >
          {isWhiteLabel ? (
            <TypographyP2SemiBold className="text-text-primary">
              {intl.formatMessage(navV3Messages.home)}
            </TypographyP2SemiBold>
          ) : (
            <BrandWordmarkSvg
              alt="Heidi"
              height={21}
              className="pointer-events-none mt-px"
              priority
            />
          )}
        </div>
        <AgentHomePanelToggle placement="panel" />
      </div>

      <SidebarProvider
        open
        onOpenChange={KEEP_PANEL_EXPANDED}
        className="min-h-0 flex-1 bg-transparent"
      >
        <NavV3 />
      </SidebarProvider>
      {/* Reads SaaS billing to decide who sees it, so not on on-prem builds. */}
      {isOnPrem ? null : (
        <Heidi2GetStartedChecklistHost ports={heidi2GetStartedChecklistPorts} />
      )}
    </PageShell.Panel>
  );
};
